/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { headerNavBarDropMenuItemSX } from '@/components/content/Header/styles/navBar/dropMenuItem';
import { HeaderNavBarMenuList } from '@/components/content/Header/parts/NavBarMenuList';
import { headerNavBarItemSX } from '@/components/content/Header/styles/navBar/item';
import { headerNavBarDropMenuSX } from '@/components/content/Header/styles/navBar/dropMenu';
import { PageLink } from '@/data/Navigation';
import { Switch } from '@/utils/switch';
import { Box, Button, ClickAwayListener, Stack, MenuItem, Grid, Skeleton } from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import React, { useCallback, useEffect } from 'react';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { styled } from '@mui/material/styles';
import { LinkWrap } from '@/components/blocks/Linkable';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { DpContainer } from '@/components/blocks/DpContainer';

type Props = {
	tree: PageLink[];
	label: string;
	url: string;
};

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))({
	[`& .${tooltipClasses.popperInteractive}`]: {
		width: '100% !important',
		backgroundColor: 'red',
	},
});

export const HeaderNavBarDropMenu = React.forwardRef(
	({ tree, label, url, ...props }: Props, ref: React.Ref<HTMLAnchorElement> | null) => {
		const router = useNextRouter();
		const [open, setOpen] = React.useState(false);
		const [newPath, setNewPath] = React.useState(true);

		const handleToolTip = useCallback(
			(action?: string) => () =>
				setOpen((open) =>
					Switch(action)
						.case('open', () => true)
						.case('close', () => false)
						.defaultTo(() => !open)
				),
			[]
		);

		useEffect(() => {
			if (!router.asPath) return;
			handleToolTip('close')();
			const timeout = setTimeout(() => {
				setNewPath(false);
			}, 1000);
			return () => clearTimeout(timeout);
		}, [handleToolTip, router.asPath]);

		const handleMouseEnter = () => {
			if (!newPath) {
				handleToolTip('open')();
				setNewPath(true);
			}
		};

		const handleMouseLeave = () => {
			handleToolTip('close')();
		};

		return (
			<ClickAwayListener onClickAway={handleToolTip('close')}>
				<Stack onMouseLeave={handleMouseLeave}>
					<NoMaxWidthTooltip
						sx={{
							position: 'absolute',
							top: '100%',
							left: 0,
							width: '100%',
							opacity: open ? 1 : 0,
							backgroundColor: 'rgba(0, 0, 0, 0.5)',
							height: '100%',
						}}
						placement="bottom-start"
						open={open}
						onClose={handleToolTip('close')}
						disableFocusListener
						disableHoverListener
						disableTouchListener
						componentsProps={{
							tooltip: {
								sx: headerNavBarDropMenuSX,
								onMouseEnter: handleMouseEnter,
								onMouseLeave: handleMouseLeave,
							},
						}}
						title={
							tree && tree.length > 0 ? (
								<Stack>
									<Stack direction="row" justifyContent="center">
										<DpContainer
											sx={{
												display: 'Grid',
												gridTemplateColumns: '1fr 2fr',
												// columnGap: '4rem',
												paddingTop: '15px',
											}}
										>
											<Grid
												display="flex"
												flexDirection="column"
												justifyContent="left"
												sx={{ mt: '40px' }}
											>
												<ContentRecommendation
													id={`DPV2_Header_${label}`}
													properties={{
														emsName: `DPV2_Header_${label}`,
													}}
													loadingElement={
														<Stack spacing={1}>
															<Skeleton variant="rounded" width={307} height={200} />
															<Skeleton variant="rounded" width={180} height={'15px'} />
															<Skeleton variant="rounded" width={100} height={'15px'} />
														</Stack>
													}
												/>
											</Grid>

											<Grid xs display="flex" justifyContent="left">
												<HeaderNavBarMenuList
													handleToolTip={handleToolTip}
													tree={tree}
													level={1}
													url={url}
												/>
											</Grid>
										</DpContainer>
									</Stack>
									<LinkWrap href={url}>
										<MenuItem component="a" sx={headerNavBarDropMenuItemSX({ isParent: false })}>
											{'Ver todo'} {label}
										</MenuItem>
									</LinkWrap>
								</Stack>
							) : null
						}
					>
						<Box onMouseEnter={handleToolTip('open')} onTouchStart={handleToolTip('close')}>
							<Button component="a" ref={ref} {...props} sx={headerNavBarItemSX}>
								{label}
							</Button>
						</Box>
					</NoMaxWidthTooltip>
				</Stack>
			</ClickAwayListener>
		);
	}
);
